import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { CreateNotification, Notification, NotificationOptions } from './Notifications.type'
import produce from 'immer'

export interface State {
  items: Notification[]
}

export const initialState: State = {
  items: [],
}

const slice = createSlice({
  name: 'global/notifications',
  initialState,
  reducers: {
    enqueueSnackbar(state, action: PayloadAction<CreateNotification>) {
      const { key: payloadKey, message, options } = action.payload
      const key = payloadKey || new Date().getTime() + Math.random()
      const notification = { key, message, options }
      state.items = addNotification(state.items, notification)
    },
    enqueueSnackbarError(
      state,
      action: PayloadAction<{ key?: string | number; message: string } & CreateNotification['options']>
    ) {
      const { key: payloadKey, message, ...options } = action.payload
      const key = payloadKey || new Date().getTime() + Math.random()
      const notification = { key, message, options: { ...options, variant: 'error' } as NotificationOptions }
      state.items = addNotification(state.items, notification)
    },
    enqueueSnackbarSuccess(
      state,
      action: PayloadAction<{ key?: string | number; message: string } & CreateNotification['options']>
    ) {
      const { key: payloadKey, message, ...options } = action.payload
      const key = payloadKey || new Date().getTime() + Math.random()
      const notification = { key, message, options: { ...options, variant: 'success' } as NotificationOptions }
      state.items = addNotification(state.items, notification)
    },
    closeSnackbar(state, action: PayloadAction<{ key?: number | string | null }>) {
      const { key } = action.payload
      const dismissAll = key == null
      state.items = state.items.map((notification) =>
        dismissAll || notification.key === key ? { ...notification, dismissed: true } : notification
      )
    },
    removeSnackbar(state, action: PayloadAction<{ key?: number | string }>) {
      const { key } = action.payload
      state.items = state.items.filter((notification) => notification.key !== key)
    },
  },
})

// reducer
export default slice.reducer

// actions
export const actions = slice.actions
export const { enqueueSnackbar, enqueueSnackbarError, enqueueSnackbarSuccess, closeSnackbar, removeSnackbar } =
  slice.actions

// action type
export type Action =
  | ReturnType<typeof enqueueSnackbar>
  | ReturnType<typeof enqueueSnackbarError>
  | ReturnType<typeof enqueueSnackbarSuccess>
  | ReturnType<typeof closeSnackbar>
  | ReturnType<typeof removeSnackbar>

// service functions
function addNotification(items: Notification[], addition: Notification) {
  return produce(items, (draft) => {
    const foundIndex = draft.findIndex((n) => n.key === addition.key)
    if (foundIndex) {
      draft.splice(foundIndex, 1, addition)
    } else {
      draft.concat([addition])
    }
  })
}
